// =============================================================================
// Origin UI — src/pages/leave/LeaveRequestPage.tsx
// Create / view / edit a leave request.
// Phase 6 — Leave Management module.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { leaveApi, type LeaveType, type LeaveRequestDetail, type WorkingDaysResponse, type LeaveOverlapWarning } from '@/services/leaveApi';
import { api } from '@/services/apiService';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader, Card, ErrorBanner, LoadingSpinner, StatusBadge } from '@/components/common';
import styles from './leave.module.css';

export function LeaveRequestPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { roles } = useAuth();

  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [allLeaveTypes, setAllLeaveTypes] = useState<LeaveType[]>([]);
  const [existing, setExisting] = useState<LeaveRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // On-behalf-of (HR Admin only)
  const [onBehalfOf, setOnBehalfOf] = useState('');
  const [employeeList, setEmployeeList] = useState<{ employeeId: string; name: string; employeeNumber: string }[]>([]);

  // Form state
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startHalf, setStartHalf] = useState(false);
  const [startHalfPeriod, setStartHalfPeriod] = useState<'AM' | 'PM'>('AM');
  const [endHalf, setEndHalf] = useState(false);
  const [endHalfPeriod, setEndHalfPeriod] = useState<'AM' | 'PM'>('PM');
  const [reason, setReason] = useState('');

  // Calculated
  const [workingDays, setWorkingDays] = useState<WorkingDaysResponse | null>(null);
  const [overlap, setOverlap] = useState<LeaveOverlapWarning | null>(null);
  const [calculating, setCalculating] = useState(false);

  // Delete draft confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Revoke confirmation (HR Admin, approved requests)
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeComments, setRevokeComments] = useState('');

  // When booking on behalf: show all active leave types
  // When self-booking: show only types the backend returned (already filtered by entitlement)
  const availableTypes = onBehalfOf ? allLeaveTypes : leaveTypes;

  // FIX: use availableTypes so on-behalf selections resolve correctly
  const selectedType = availableTypes.find(t => t.leaveTypeId === leaveTypeId);

  useEffect(() => { loadInitialData(); }, [roles.isHRAdmin]);

  // Recalculate working days when dates change
  useEffect(() => {
    if (startDate && endDate && startDate <= endDate) {
      calculateDays();
    } else {
      setWorkingDays(null);
      setOverlap(null);
    }
  }, [startDate, endDate, startHalf, startHalfPeriod, endHalf, endHalfPeriod]);

  async function loadInitialData() {
    try {
      setLoading(true);
      const types = await leaveApi.getLeaveTypes();
      setLeaveTypes(types.filter(t => t.isActive));

      // Load all leave types for on-behalf dropdown (HR Admin only)
      if (roles.isHRAdmin && isNew) {
        try {
          const allTypes = await leaveApi.getAllLeaveTypes();
          setAllLeaveTypes(allTypes.filter(t => t.isActive));
        } catch { /* fallback to filtered types */ }
      }

      // Load employee list for on-behalf-of dropdown (HR Admin only)
      if (roles.isHRAdmin && isNew) {
        try {
          const emps = await api.get<any[]>('/api/employees');
          setEmployeeList(
            emps
              .filter(e => e.employmentStatus === 'Active')
              .map(e => ({ employeeId: e.employeeId, name: `${e.firstName} ${e.lastName}`, employeeNumber: e.employeeNumber }))
              .sort((a, b) => a.name.localeCompare(b.name))
          );
        } catch { /* employee list optional */ }
      }

      if (id) {
        const req = await leaveApi.getRequest(id);
        setExisting(req);
        setLeaveTypeId(req.leaveTypeCode ? types.find(t => t.code === req.leaveTypeCode)?.leaveTypeId || '' : '');
        setStartDate(req.startDate.split('T')[0]);
        setEndDate(req.endDate.split('T')[0]);
        setStartHalf(req.startDateIsHalfDay);
        setStartHalfPeriod((req.startDateHalfDayPeriod as 'AM' | 'PM') || 'AM');
        setEndHalf(req.endDateIsHalfDay);
        setEndHalfPeriod((req.endDateHalfDayPeriod as 'AM' | 'PM') || 'PM');
        setReason(req.reason || '');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function calculateDays() {
    try {
      setCalculating(true);
      const [wd, ol] = await Promise.all([
        leaveApi.calculateWorkingDays({
          startDate, endDate,
          startDateIsHalfDay: startHalf,
          startDateHalfDayPeriod: startHalf ? startHalfPeriod : null,
          endDateIsHalfDay: endHalf,
          endDateHalfDayPeriod: endHalf ? endHalfPeriod : null,
          employeeId: onBehalfOf || undefined,
        }),
        leaveApi.checkOverlap(startDate, endDate, onBehalfOf || undefined),
      ]);
      setWorkingDays(wd);
      setOverlap(ol);
    } catch {
      // Silently fail on calculation — form still works
    } finally {
      setCalculating(false);
    }
  }

  async function handleSave(andSubmit: boolean) {
    if (!leaveTypeId || !startDate || !endDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const data: any = {
        leaveTypeId,
        startDate,
        endDate,
        startDateIsHalfDay: startHalf,
        startDateHalfDayPeriod: startHalf ? startHalfPeriod : null,
        endDateIsHalfDay: endHalf,
        endDateHalfDayPeriod: endHalf ? endHalfPeriod : null,
        reason,
      };

      // HR Admin booking on behalf
      if (onBehalfOf) {
        data.onBehalfOfEmployeeId = onBehalfOf;
      }

      let result: LeaveRequestDetail;
      if (isNew) {
        result = await leaveApi.createRequest(data);
      } else {
        result = await leaveApi.updateRequest(id!, data);
      }

      if (onBehalfOf) {
        const empName = employeeList.find(e => e.employeeId === onBehalfOf)?.name || 'employee';
        setSuccess(`Leave recorded for ${empName}`);
      } else if (andSubmit) {
        result = await leaveApi.submitRequest(result.leaveRequestId);
        setSuccess('Leave request submitted for approval');
      } else {
        setSuccess('Leave request saved as draft');
      }

      setTimeout(() => navigate('/leave'), 1500);
    } catch (err: any) {
      setError(err.body ? JSON.parse(err.body).message || err.message : err.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel() {
    if (!id) return;
    try {
      setSubmitting(true);
      await leaveApi.cancelRequest(id);
      setSuccess('Leave request cancelled');
      setTimeout(() => navigate('/leave'), 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteDraft() {
    if (!id) return;
    try {
      setSubmitting(true);
      setError('');
      await leaveApi.cancelRequest(id);
      setSuccess('Draft deleted');
      setTimeout(() => navigate('/leave'), 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft');
    } finally {
      setSubmitting(false);
      setShowDeleteConfirm(false);
    }
  }

  async function handleRevoke() {
    if (!id) return;
    try {
      setSubmitting(true);
      setError('');
      await leaveApi.revokeRequest(id, revokeComments || undefined);
      setSuccess('Leave revoked — the days have been returned to the balance');
      setTimeout(() => navigate('/leave'), 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to revoke leave');
    } finally {
      setSubmitting(false);
      setShowRevokeConfirm(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  // View mode for non-draft requests
  const isViewOnly = existing && existing.status !== 'Draft' && existing.status !== 'Submitted';
  const isEditingDraft = existing && existing.status === 'Draft';
  const isEditingSubmitted = existing && existing.status === 'Submitted';

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Leave Request' : isViewOnly ? 'Leave Request' : isEditingSubmitted ? 'Edit Submitted Request' : 'Edit Leave Request'}
        subtitle={existing ? `${existing.leaveTypeName} — ${existing.status}` : undefined}
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && <div className={styles.successBanner}>{success}</div>}

      {/* ─── Delete Draft Confirmation ─── */}
      {showDeleteConfirm && (
        <div className={styles.confirmOverlay} onClick={() => setShowDeleteConfirm(false)}>
          <div className={styles.confirmDialog} onClick={e => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>Delete Draft</h3>
            <p className={styles.confirmMessage}>
              Are you sure you want to delete this draft leave request? This cannot be undone.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowDeleteConfirm(false)} disabled={submitting}>
                Go Back
              </button>
              <button className={styles.btnDanger} onClick={handleDeleteDraft} disabled={submitting}>
                {submitting ? 'Deleting...' : 'Delete Draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Revoke Leave Request Confirmation (HR Admin) ─── */}
      {showRevokeConfirm && (
        <div className={styles.confirmOverlay} onClick={() => setShowRevokeConfirm(false)}>
          <div className={styles.confirmDialog} onClick={e => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>Revoke Leave Request</h3>
            <p className={styles.confirmMessage}>
              This will revoke the leave request and return any deducted days to the balance.
              A manager does not need to approve a revocation. This is recorded in the audit log.
            </p>
            <div className={styles.formGroup} style={{ marginTop: 12 }}>
              <label className={styles.formLabel}>Reason (optional)</label>
              <textarea
                className={styles.formTextarea}
                rows={2}
                value={revokeComments}
                onChange={e => setRevokeComments(e.target.value)}
                placeholder="Why is this leave being revoked?"
              />
            </div>
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowRevokeConfirm(false)} disabled={submitting}>
                Go Back
              </button>
              <button className={styles.btnDanger} onClick={handleRevoke} disabled={submitting}>
                {submitting ? 'Revoking...' : 'Revoke Leave'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── View Mode ─── */}
      {isViewOnly && existing && (
        <Card>
          <div className={styles.detailGrid}>
            <div className={styles.detailRow}><span className={styles.detailLabel}>Status</span><StatusBadge status={existing.status} /></div>
            <div className={styles.detailRow}><span className={styles.detailLabel}>Leave Type</span><span>{existing.leaveTypeName}</span></div>
            <div className={styles.detailRow}><span className={styles.detailLabel}>Start Date</span><span>{new Date(existing.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}{existing.startDateIsHalfDay ? ` (${existing.startDateHalfDayPeriod})` : ''}</span></div>
            <div className={styles.detailRow}><span className={styles.detailLabel}>End Date</span><span>{new Date(existing.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}{existing.endDateIsHalfDay ? ` (${existing.endDateHalfDayPeriod})` : ''}</span></div>
            <div className={styles.detailRow}><span className={styles.detailLabel}>Working Days</span><span className={styles.daysValue}>{existing.totalWorkingDays}</span></div>
            {existing.reason && <div className={styles.detailRow}><span className={styles.detailLabel}>Reason</span><span>{existing.reason}</span></div>}
            {existing.approverName && <div className={styles.detailRow}><span className={styles.detailLabel}>Reviewed by</span><span>{existing.approverName}</span></div>}
            {existing.approverComments && <div className={styles.detailRow}><span className={styles.detailLabel}>Comments</span><span>{existing.approverComments}</span></div>}
            {existing.cancellationReason && <div className={styles.detailRow}><span className={styles.detailLabel}>Cancellation Reason</span><span>{existing.cancellationReason}</span></div>}
          </div>
          <div className={styles.formActions}>
            {/* Revoke (HR Admin) — left-aligned, only for approved requests */}
            {roles.isHRAdmin && (existing.status === 'Approved' || existing.status === 'Submitted') && (
              <button
                className={styles.btnDanger}
                onClick={() => setShowRevokeConfirm(true)}
                disabled={submitting}
                style={{ marginRight: 'auto' }}
              >
                Revoke Leave
              </button>
            )}
            <button className={styles.btnSecondary} onClick={() => navigate('/leave')}>Back</button>
            {(existing.status === 'Submitted' || (existing.status === 'Approved' && new Date(existing.startDate) > new Date())) && (
              <button className={styles.btnDanger} onClick={handleCancel} disabled={submitting}>
                {submitting ? 'Cancelling...' : 'Cancel Request'}
              </button>
            )}
          </div>
        </Card>
      )}

      {/* ─── Edit / Create Mode ─── */}
      {!isViewOnly && (
        <Card>
          <div className={styles.formGrid}>
            {/* On-behalf-of employee picker (HR Admin only, new requests only) */}
            {roles.isHRAdmin && isNew && (
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Book on behalf of employee</label>
                <select className={styles.formSelect} value={onBehalfOf} onChange={e => { setOnBehalfOf(e.target.value); setLeaveTypeId(''); }}>
                  <option value="">Myself (default)</option>
                  {employeeList.map(e => (
                    <option key={e.employeeId} value={e.employeeId}>
                      {e.name} ({e.employeeNumber})
                    </option>
                  ))}
                </select>
                {onBehalfOf && (
                  <span style={{ fontSize: '12px', color: '#FF8300', fontWeight: 600, marginTop: '4px', display: 'block' }}>
                    Retrospective or notification-type leave (sick, compassionate, etc.) will be auto-approved. Future annual/TOIL/unpaid leave will go to the employee's manager for approval.
                  </span>
                )}
              </div>
            )}

            {/* Leave Type */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Leave Type *</label>
              <select className={styles.formSelect} value={leaveTypeId} onChange={e => setLeaveTypeId(e.target.value)}>
                <option value="">Select leave type...</option>
                {availableTypes.map(t => (
                  <option key={t.leaveTypeId} value={t.leaveTypeId}>
                    {t.name} {t.isPaid ? '(Paid)' : '(Unpaid)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Dates */}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Start Date *</label>
                <input type="date" className={styles.formInput} value={startDate}
                  min={undefined}
                  onChange={e => { setStartDate(e.target.value); if (!endDate || e.target.value > endDate) setEndDate(e.target.value); }} />
                {selectedType?.allowHalfDay && (
                  <div className={styles.halfDayToggle}>
                    <label><input type="checkbox" checked={startHalf} onChange={e => setStartHalf(e.target.checked)} /> Half day</label>
                    {startHalf && (
                      <select className={styles.halfDaySelect} value={startHalfPeriod} onChange={e => setStartHalfPeriod(e.target.value as 'AM' | 'PM')}>
                        <option value="AM">Morning (AM)</option>
                        <option value="PM">Afternoon (PM)</option>
                      </select>
                    )}
                  </div>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>End Date *</label>
                <input type="date" className={styles.formInput} value={endDate}
                  min={startDate || undefined}
                  onChange={e => setEndDate(e.target.value)} />
                {selectedType?.allowHalfDay && startDate !== endDate && (
                  <div className={styles.halfDayToggle}>
                    <label><input type="checkbox" checked={endHalf} onChange={e => setEndHalf(e.target.checked)} /> Half day</label>
                    {endHalf && (
                      <select className={styles.halfDaySelect} value={endHalfPeriod} onChange={e => setEndHalfPeriod(e.target.value as 'AM' | 'PM')}>
                        <option value="AM">Morning (AM)</option>
                        <option value="PM">Afternoon (PM)</option>
                      </select>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Working Days Calculation */}
            {workingDays && (
              <div className={styles.workingDaysBox}>
                <span className={styles.workingDaysNumber}>{workingDays.workingDays}</span>
                <span> working day{workingDays.workingDays !== 1 ? 's' : ''}</span>
                {workingDays.publicHolidaysInRange.length > 0 && (
                  <span className={styles.muted}> ({workingDays.publicHolidaysInRange.length} public holiday{workingDays.publicHolidaysInRange.length > 1 ? 's' : ''} excluded)</span>
                )}
                {calculating && <span className={styles.muted}> calculating...</span>}
              </div>
            )}

            {/* Overlap Warning */}
            {overlap?.isWarning && (
              <div className={styles.warningBanner}>
                ⚠️ {overlap.warningMessage}
                <ul className={styles.overlapList}>
                  {overlap.peopleOff.map((p, i) => (
                    <li key={i}>{p.employeeName} — {p.leaveTypeName} ({new Date(p.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – {new Date(p.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Reason */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Reason</label>
              <textarea className={styles.formTextarea} rows={3} value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Optional — reason for leave" />
            </div>
          </div>

          {/* Actions */}
          <div className={styles.formActions}>
            {/* Delete Draft button — left-aligned, only for existing drafts */}
            {isEditingDraft && (
              <button
                className={styles.btnDanger}
                onClick={() => setShowDeleteConfirm(true)}
                disabled={submitting}
                style={{ marginRight: 'auto' }}
              >
                Delete Draft
              </button>
            )}
            <button className={styles.btnSecondary} onClick={() => navigate('/leave')} disabled={submitting}>Cancel</button>
            {onBehalfOf ? (
              <button className={styles.createBtn} onClick={() => handleSave(false)} disabled={submitting || !leaveTypeId || !startDate || !endDate}>
                {submitting ? 'Recording...' : 'Record Leave'}
              </button>
            ) : isEditingSubmitted ? (
              <button className={styles.createBtn} onClick={() => handleSave(false)} disabled={submitting || !leaveTypeId || !startDate || !endDate}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            ) : (
              <>
                <button className={styles.btnSecondary} onClick={() => handleSave(false)} disabled={submitting}>Save Draft</button>
                <button className={styles.createBtn} onClick={() => handleSave(true)} disabled={submitting || !leaveTypeId || !startDate || !endDate}>
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </button>
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
